import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MenuItems } from '../enums/MenuItems'
import LayoutSplitView from '../components/layout/LayoutSplitView'
import FieldSearchEditor from '../components/candidateSearch/FieldSearchEditor'
import Button from '../components/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faChartBar,
    faFilter,
    faHexagonNodes,
    faLink,
    faList,
    faPlusCircle,
    faSearch,
} from '@fortawesome/free-solid-svg-icons'
import HelixSearchFilters from '../components/helixSearch/HelixSearchFilters'
import HelixSearchInsights from '../components/helixSearch/HelixSearchInsights'
import { PersonDto, PersonLiteDto } from '../api/person/models/PersonDto'
import personApi from '../api/person/PersonApi'
import NetworkLinks from '../components/NetworkLinks'
import AddNetworkLinksModal from '../components/AddNetworkLinksModal'
import { parse } from 'lucene-query-parser'
import { StartNewHelixSearchRes } from '../api/person/models/startNewHelixSearch/StartNewHelixSearchRes'
import {
    GetHelixSearchSessionRes,
    HelixSearchSessionStatus,
    HelixSearchSessionSummary,
} from '../api/person/models/getHelixSearchSession/GetHelixSearchSessionRes'
import HelixCandidateResultList from '../components/helixSearch/HelixCandidateResultList'
import { HelixCandidateLiteDto } from '../api/role/models/CandidateDto'
import { MapExtra } from '../utils/MapExtra'
import ToggleSwitch3 from '../components/ToggleSwitch3'
import networkApi from '../api/network/NetworkApi'
import { ResolvedPersonLiteDto } from '../api/network/models/getNetworkScopedDtos'
import HelixSearchProgressModal from '../components/helixSearch/HelixSearchProgressModal'
import Note from '../components/Note'
import { NoteType } from '../enums/NoteType'
import { CompanyDto } from '../api/company/models/CompanyDto'
import companyApi from '../api/company/CompanyApi'
import {
    ShortlistFilterValues,
    SortBy,
    SortByDirection,
} from '../api/person/models/getHelixSearchResultsCountOnly/GetHelixSearchResultsCountOnlyDtos'
import { HelixSearchResultDto } from '../api/person/models/getHelixSearchResults/GetHelixSearchResultsRes'
import HelixSearchNetworkView from '../components/helixSearch/HelixSearchNetworkView'
import HelixSearchDataLoadingModal from '../components/helixSearch/HelixSearchDataLoadingModal'
import HelixBooleanSearchHelp from '../components/helixSearch/rightPanes/HelixBooleanSearchHelp'
import HelixCandidateRightPane from '../components/helixSearch/rightPanes/HelixCandidateRightPane'
import { StartNewHelixSearchReq } from '../api/person/models/startNewHelixSearch/StartNewHelixSearchReq'
import { UpdateHelixSearchReq } from '../api/person/models/updateHelixSearch/UpdateHelixSearchReq'

type FilterData = {
    currentPageNumber: number
    perPage: number
    networkLinksSelected: string[]
    minRelationshipScore: string
    mustBeInBooleanSearchResults: boolean
    mustHaveRelationship: boolean
    shortlistedSelected: string[]
    orderBy: string
}

export default function HelixSearch(): React.JSX.Element {
    const urlParams = useMemo(() => new URLSearchParams(location.search), [])
    const [roleId] = useState<string | null>(null)
    const navigate = useNavigate()

    const insightsRef = useRef<HTMLDivElement>(null)
    const personLiteCacheRef = useRef(new MapExtra<string, PersonLiteDto>())
    const personCacheRef = useRef(new MapExtra<string, PersonDto>())
    const companyCacheRef = useRef(new MapExtra<string, CompanyDto>())
    const [resolvedPersonLites, setResolvedPersonLites] = useState<
        ResolvedPersonLiteDto[]
    >([])

    const [query, setQuery] = React.useState<string>(
        `(AWS OR "Amazon Web Services")
    AND devops
    AND (finance OR "financial services" OR banking OR fintech)
    AND NOT (currentCompany:"Example Ltd")`,
    )
    const [ignoreRetiredPeople, setIgnoreRetiredPeople] =
        React.useState<boolean>(true)
    const [showResults, setShowResults] = React.useState<boolean>(false)

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [
        networkLinksIdsSelectedInNetworkLinks,
        setNetworkLinksIdsSelectedInNetworkLinks,
    ] = useState<string[]>([])
    const [
        networkLinksSelectedInNetworkLinks,
        setNetworkLinksSelectedInNetworkLinks,
    ] = useState<PersonLiteDto[]>([])
    // used by the filters to show the available network links
    //  only set once a search is initiated
    const [networkLinkIdsSetInThisSearch, setNetworkLinkIdsSetInThisSearch] =
        useState<string[]>([])
    const [searchSession, setSearchSession] =
        useState<GetHelixSearchSessionRes>()

    const [showSearchProgress, setShowSearchProgress] = useState(false)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)

    const [searchResults, setSearchResults] = useState<HelixSearchResultDto[]>(
        [],
    )
    const [rightPaneMode, setRightPaneMode] = useState<
        | 'boolean_search_help'
        | 'saved_searches'
        | 'candidate'
        | 'network_seed'
        | 'company'
    >('boolean_search_help')
    const [selectedCandidateId, setSelectedCandidateId] = useState<
        string | null
    >(null)
    const [helixCandidateLiteDtos, setHelixCandidateLiteDtos] = useState<
        HelixCandidateLiteDto[]
    >([])

    // ##### filter state - start #####
    // const filtersRef = useRef(() => getCurrentFilters())
    const [networkLinksSelectedInFilters, setNetworkLinksSelectedInFilters] =
        useState<string[]>([])
    const [shortlistedSelected, setShortlistedSelected] = useState<string[]>([
        'blanks',
    ])
    const [minRelationshipScore, setMinRelationshipScore] = useState<string>(
        'min_relationship_score_any',
    )
    const [orderBy, setOrderBy] = useState<string>('combined_desc')
    const [mustBeInBooleanSearchResults, setMustBeInBooleanSearchResults] =
        useState<boolean>(true)
    const [mustHaveRelationship, setMustHaveRelationship] =
        useState<boolean>(true)
    const [filteredCandidatePoolCount, setFilteredCandidatePoolCount] =
        useState<number>(0)
    const [
        filteredCandidatePoolCountIsLoading,
        setFilteredCandidatePoolCountIsLoading,
    ] = useState<boolean>(false)
    const [filtersAreDirty, setFiltersAreDirty] = useState<boolean>(false)
    const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(50)
    const [totalResultCount, setTotalResultCount] = useState<number>(0)
    const [showDataLoading, setShowDataLoading] = useState<boolean>(false)
    // ##### filter state - end #####

    // const urlRoleId = urlParams.get('roleId')

    // ############## NEW CONTROL FLOW - start ##############

    // WARNING: This useEffect that sets the filterRef MUST MUST MUST live at the top!
    //  React will fire useEffects in the order they are declared and other useEffects rely on the filterRef
    //  being up to date before they run. This is pretty horrible I know but it works.
    // this is a great react trick! - start
    // we use a ref to store the current filter state
    // and a useEffect to update it whenever the filter state changes
    // then functions and components can access the current filter state without needing to depend on the
    //  useState variables. It avoids things being re-declared which triggers re-renders etc...
    const filterRef = useRef<FilterData>({
        currentPageNumber,
        perPage,
        networkLinksSelected: networkLinksSelectedInFilters,
        minRelationshipScore,
        mustBeInBooleanSearchResults,
        mustHaveRelationship,
        shortlistedSelected,
        orderBy,
    })

    useEffect(() => {
        // console.log(
        //     `Updating filterRef.current with current filter state. OLD filter state:`,
        //     filterRef.current,
        // )

        filterRef.current = {
            currentPageNumber,
            perPage,
            networkLinksSelected: networkLinksSelectedInFilters,
            minRelationshipScore,
            mustBeInBooleanSearchResults,
            mustHaveRelationship,
            shortlistedSelected,
            orderBy,
        }

        // console.log(
        //     `Updating filterRef.current with current filter state. NEW filter state:`,
        //     filterRef.current,
        // )
    }, [
        currentPageNumber,
        perPage,
        networkLinksSelectedInFilters,
        minRelationshipScore,
        mustBeInBooleanSearchResults,
        mustHaveRelationship,
        shortlistedSelected,
        orderBy,
    ])
    // this is a great react trick! - end

    const resetFilters = useCallback(
        (selectedNetworkLinkIds: string[]): FilterData => {
            // console.log(
            //     `Resetting filters with selectedNetworkLinkIds:`,
            //     selectedNetworkLinkIds,
            // )

            setCurrentPageNumber(1)
            setNetworkLinksSelectedInFilters(selectedNetworkLinkIds)
            setShortlistedSelected(['blanks'])
            setMinRelationshipScore('min_relationship_score_any')
            setOrderBy('combined_desc')
            setMustBeInBooleanSearchResults(true)
            setMustHaveRelationship(true)
            setFiltersAreDirty(false)

            return {
                currentPageNumber: 1,
                perPage,
                networkLinksSelected: selectedNetworkLinkIds,
                minRelationshipScore: 'min_relationship_score_any',
                mustBeInBooleanSearchResults: true,
                mustHaveRelationship: true,
                shortlistedSelected: ['blanks'],
                orderBy: 'combined_desc',
            }
        },
        [
            setCurrentPageNumber,
            setNetworkLinksSelectedInFilters,
            setShortlistedSelected,
            setMinRelationshipScore,
            setOrderBy,
            setMustBeInBooleanSearchResults,
            setMustHaveRelationship,
            setFiltersAreDirty,
            perPage,
        ],
    )

    const loadResults = useCallback(
        async (
            sessionSummary: HelixSearchSessionSummary,
            filters: FilterData,
            showLoading: boolean = false,
        ) => {
            // console.log(
            //     `######## loadResults called with sessionSummary: ${sessionSummary} and filters: ${JSON.stringify(
            //         filters,
            //     )}`,
            // )

            if (showLoading) {
                setShowDataLoading(true)
            }

            try {
                const searchResultsNew = await personApi.getHelixSearchResults({
                    searchSessionToken: sessionSummary.sst,
                    pageNumber: filters.currentPageNumber,
                    perPage: filters.perPage,
                    filterOptions: {
                        networkSeedPersonIds: filters.networkLinksSelected,
                        minRelationshipScore:
                            convertMinRelationshipScoreToNumber(
                                filters.minRelationshipScore,
                            ),
                        mustBeInBooleanSearchResults:
                            filters.mustBeInBooleanSearchResults,
                        mustHaveRelationship: filters.mustHaveRelationship,
                        roleContextFilters: {
                            shortlistedValues:
                                convertShortlistedSelectedToFilterValue(
                                    filters.shortlistedSelected,
                                ),
                        },
                    },
                    sortOptions: convertOrderByToSortBy(filters.orderBy),
                })

                setTotalResultCount(searchResultsNew.totalCount)

                if (searchResultsNew.results.length > 0) {
                    const candidatePersonIds = searchResultsNew.results.map(
                        (r) => r.personId,
                    )
                    const persons = await personApi.getPersonsById(
                        candidatePersonIds,
                    )

                    for (const person of persons.persons) {
                        if (!personCacheRef.current.has(person.id)) {
                            personCacheRef.current.set(person.id, person)
                        }
                    }

                    if (filters.networkLinksSelected.length > 0) {
                        const resolvedPersonLites = (
                            await networkApi.getNetworkScoped({
                                seedPersonIds: filters.networkLinksSelected,
                                resolvedPersonIds: candidatePersonIds,
                            })
                        ).resolvedPersonLites
                        setResolvedPersonLites(resolvedPersonLites)
                    }

                    const companyIds = new Set<string>()
                    for (const person of persons.persons) {
                        for (const workedAt of person.workedAts ?? []) {
                            companyIds.add(workedAt.companyId)
                        }
                    }

                    const companyCache = companyCacheRef.current
                    const missingCompanyIds = Array.from(companyIds).filter(
                        (id) => !companyCache.has(id),
                    )

                    if (missingCompanyIds.length > 0) {
                        const companies = await companyApi.getCompanies({
                            companyIds: missingCompanyIds,
                        })
                        for (const company of companies.companies) {
                            companyCache.set(company.id, company)
                        }
                    }
                }

                setSearchResults(searchResultsNew.results)
                setHelixCandidateLiteDtos(
                    searchResultsNew.results.map(
                        (result): HelixCandidateLiteDto => ({
                            candidatePersonId: result.personId,
                            present: undefined, // TODO: 77 - only populated if we're in the context of a role
                            rank: result.rank,
                            booleanSearchScoreNormalised:
                                result.booleanSearchScoreNormalised,
                            aggregatedRelationshipScore:
                                result.aggregatedRelationshipScore,
                            combinedScore: result.combinedScore,
                        }),
                    ),
                )
                setFiltersAreDirty(false)
                setShowResults(true)
            } catch (error) {
                console.error('Error loading search results:', error)
                setErrorMsg('Failed to load search results.')
            } finally {
                if (showLoading) {
                    setShowDataLoading(false)
                }
            }
        },
        [],
    )
    const pollForSearchCompletion = useCallback(
        async (sst: string, filters: FilterData) => {
            setShowResults(false)
            setShowSearchProgress(true)
            setErrorMsg(null)

            // if we're polling then we're starting a new search so we need to reset the filters
            //  and use all the available network links in this new search
            const session1 = await personApi.getHelixSearchSession(sst)
            setSearchSession(session1)
            setNetworkLinksIdsSelectedInNetworkLinks(
                session1.sessionSummary.networkSeedPersonIds ?? [],
            )
            setNetworkLinkIdsSetInThisSearch(
                session1.sessionSummary.networkSeedPersonIds ?? [],
            )
            setQuery(session1.sessionSummary.booleanSearchQuery ?? '')
            resetFilters(session1.sessionSummary.networkSeedPersonIds ?? [])
            // manually set the filters to the current state
            filters.networkLinksSelected =
                session1.sessionSummary.networkSeedPersonIds ?? []

            const poll = async () => {
                // console.log(`Polling for search session: ${sst}`)
                try {
                    const session = await personApi.getHelixSearchSession(sst)
                    setSearchSession(session)

                    switch (session.sessionSummary.status) {
                        case 'COMPLETE':
                            clearInterval(interval)
                            await loadResults(session.sessionSummary, filters)
                            break
                        case 'ERROR':
                            clearInterval(interval)
                            setShowSearchProgress(false)
                            setErrorMsg(
                                'There was an error performing the search. Please try again.',
                            )
                            break
                    }
                } catch (err) {
                    clearInterval(interval)
                    setShowSearchProgress(false)
                    setErrorMsg(
                        'There was an error performing the search. Please try again. (polling error)',
                    )
                    console.error(err)
                }
            }

            const interval = setInterval(poll, 3000)
            await poll()
        },
        [loadResults, resetFilters],
    )

    const pageLoadRan = useRef<boolean>(false)
    useEffect(() => {
        if (pageLoadRan.current === true) {
            // console.log('@@ page load already ran, skipping')
            return
        }

        // console.log('@@@@@@@@@ page load!')

        pageLoadRan.current = true
        const urlParams = new URLSearchParams(location.search)
        const sst = urlParams.get('sst')

        if (sst) {
            // console.log('Found existing sst:', sst)
            pollForSearchCompletion(sst, {
                currentPageNumber,
                perPage,
                networkLinksSelected: networkLinksSelectedInFilters,
                minRelationshipScore,
                mustBeInBooleanSearchResults,
                mustHaveRelationship,
                shortlistedSelected,
                orderBy,
            })
        } else {
            // console.log('No sst in URL')
            // Optionally do something if no sst exists
        }
    }, [
        currentPageNumber,
        minRelationshipScore,
        mustBeInBooleanSearchResults,
        mustHaveRelationship,
        networkLinksSelectedInFilters,
        orderBy,
        perPage,
        pollForSearchCompletion,
        shortlistedSelected,
    ])

    async function search() {
        resetResults()
        setErrorMsg(null)

        try {
            let queryString = query.trim()

            if (
                queryString === '' &&
                networkLinksIdsSelectedInNetworkLinks.length === 0
            ) {
                setErrorMsg(
                    'Please enter a search query or select network links.',
                )
                return
            }

            if (ignoreRetiredPeople) {
                queryString = `(${queryString}) AND NOT (headline:"retired" OR summary:"retired" OR jobTitle:"retired" OR company:"retired" OR jobDescription:"retired")`
            }

            parse(queryString) // validate query syntax
        } catch (error: unknown) {
            if (error instanceof Error) {
                setErrorMsg(`Boolean query syntax is invalid. ${error.message}`)
            } else {
                setErrorMsg('Boolean query syntax is invalid.')
            }
            // console.error(
            //     `Lucene parsing error for query "${query}" Error:`,
            //     error,
            // )
            return
        }

        try {
            let sst = urlParams.get('sst')
            let startRes: StartNewHelixSearchRes

            if (!sst) {
                const startNewReq: StartNewHelixSearchReq = {
                    roleId: roleId ?? undefined,
                }

                if (query.trim() !== '') {
                    startNewReq.booleanSearchEvalParams = {
                        booleanSearchQuery: query,
                        autoIgnoreRetiredPeople: ignoreRetiredPeople,
                    }
                }

                if (networkLinksIdsSelectedInNetworkLinks.length > 0) {
                    startNewReq.relationshipEvalParams = {
                        networkSeedPersonIds:
                            networkLinksIdsSelectedInNetworkLinks,
                    }
                }

                startRes = await personApi.startNewHelixSearch(startNewReq)
                sst = startRes.searchSessionToken
                urlParams.set('sst', sst)
                navigate(`?${urlParams.toString()}`, { replace: true })
            } else {
                const updateReq: UpdateHelixSearchReq = {
                    sst,
                    roleId: roleId ?? undefined,
                }

                if (query.trim() !== '') {
                    updateReq.booleanSearchEvalParams = {
                        booleanSearchQuery: query,
                        autoIgnoreRetiredPeople: ignoreRetiredPeople,
                    }
                }

                if (networkLinksIdsSelectedInNetworkLinks.length > 0) {
                    updateReq.relationshipEvalParams = {
                        networkSeedPersonIds:
                            networkLinksIdsSelectedInNetworkLinks,
                    }
                }

                await personApi.updateHelixSearch(updateReq)
            }

            resetFilters(networkLinksIdsSelectedInNetworkLinks)

            await pollForSearchCompletion(sst, {
                currentPageNumber,
                perPage,
                networkLinksSelected: networkLinksSelectedInFilters,
                minRelationshipScore,
                mustBeInBooleanSearchResults,
                mustHaveRelationship,
                shortlistedSelected,
                orderBy,
            })
        } catch (error) {
            setErrorMsg('Search failed.')
            console.error('Search failed:', error)
        }
    }

    // ############## NEW CONTROL FLOW - end ##############

    // fired when the selected network links change
    useEffect(() => {
        // console.log(
        //     `Network links changed: ${networkLinksIdsSelectedInNetworkLinks}`,
        // )

        async function fetchNetworkLinks() {
            if (networkLinksIdsSelectedInNetworkLinks.length === 0) {
                setNetworkLinksSelectedInNetworkLinks([])
                return
            }

            // Determine which personIds are missing from the cache
            const cache = personLiteCacheRef.current
            const missingIds = networkLinksIdsSelectedInNetworkLinks.filter(
                (id) => !cache.has(id),
            )

            // No need to fetch if everything is already cached
            if (missingIds.length === 0) {
                const cachedPersons = networkLinksIdsSelectedInNetworkLinks.map(
                    (id) => cache.getOrThrow(id),
                )
                setNetworkLinksSelectedInNetworkLinks(cachedPersons)
                return
            }

            try {
                const response = await personApi.getPersonsLite({
                    personIds: missingIds,
                })

                // Update the cache with new data
                for (const person of response.persons) {
                    cache.set(person.id, person)
                }

                // Always build the list from the full input (cached + new)
                const fullList = networkLinksIdsSelectedInNetworkLinks
                    .map((id) => cache.get(id))
                    .filter(Boolean) as PersonLiteDto[]

                setNetworkLinksSelectedInNetworkLinks(fullList)
            } catch (error) {
                console.error('Failed to fetch missing network links:', error)
            }
        }

        fetchNetworkLinks()
    }, [networkLinksIdsSelectedInNetworkLinks])

    function resetResults() {
        setShowResults(false)
        setSearchResults([])
        setHelixCandidateLiteDtos([])
    }

    async function addNetworkLinksModalClosed(
        newIds: string[],
        confirmed: boolean,
    ) {
        if (confirmed && newIds.length > 0) {
            setNetworkLinksIdsSelectedInNetworkLinks((prev) => [
                ...new Set([...prev, ...newIds]),
            ])
        }
        setIsModalOpen(false)
    }

    function handleRemoveNetworkLink(idToRemove: string) {
        setNetworkLinksIdsSelectedInNetworkLinks((prev) =>
            prev.filter((id) => id !== idToRemove),
        )
        setNetworkLinksSelectedInNetworkLinks((prev) =>
            prev.filter((person) => person.id !== idToRemove),
        )
    }

    function handleLiveNetworkLinkSelection(
        person: PersonLiteDto,
        selected: boolean,
    ) {
        const id = person.id

        if (selected) {
            setNetworkLinksIdsSelectedInNetworkLinks((prev) => [
                ...new Set([...prev, id]),
            ])
            setNetworkLinksSelectedInNetworkLinks((prev) => {
                if (prev.some((p) => p.id === id)) return prev
                return [...prev, person]
            })
        } else {
            setNetworkLinksIdsSelectedInNetworkLinks((prev) =>
                prev.filter((existingId) => existingId !== id),
            )
            setNetworkLinksSelectedInNetworkLinks((prev) =>
                prev.filter((p) => p.id !== id),
            )
        }
    }

    // ##### filter functions - start #####
    const latestRequestIdRef = useRef(0)

    const getFilteredCandidatePoolCount = useCallback(async () => {
        if (
            searchSession === undefined ||
            searchSession.sessionSummary.status !==
                HelixSearchSessionStatus.COMPLETE
        ) {
            // console.log(
            //     `Skipping filtered candidate pool count fetch, search session not complete.`,
            // )
            return
        }

        const requestId = ++latestRequestIdRef.current

        setFilteredCandidatePoolCountIsLoading(true)

        const filters = filterRef.current

        const sst = urlParams.get('sst')
        if (!sst) {
            setFilteredCandidatePoolCountIsLoading(false)
            return
        }

        const orderByData = convertOrderByToSortBy(filters.orderBy)

        try {
            const res = await personApi.getHelixSearchResultsCountOnly({
                searchSessionToken: sst,
                filterOptions: {
                    networkSeedPersonIds: filters.networkLinksSelected,
                    mustBeInBooleanSearchResults:
                        filters.mustBeInBooleanSearchResults,
                    mustHaveRelationship: filters.mustHaveRelationship,
                    minRelationshipScore: convertMinRelationshipScoreToNumber(
                        filters.minRelationshipScore,
                    ),
                    roleContextFilters: {
                        shortlistedValues:
                            convertShortlistedSelectedToFilterValue(
                                filters.shortlistedSelected,
                            ),
                    },
                },
                sortOptions: {
                    sortBy: orderByData.sortBy,
                    direction: orderByData.direction,
                },
            })

            if (requestId === latestRequestIdRef.current) {
                setFilteredCandidatePoolCount(res.totalCount)
            }
        } catch (err) {
            if (requestId === latestRequestIdRef.current) {
                console.error(
                    'Failed to get filtered candidate pool count:',
                    err,
                )
            }
        } finally {
            if (requestId === latestRequestIdRef.current) {
                setFilteredCandidatePoolCountIsLoading(false)
            }
        }
    }, [searchSession, urlParams])

    // automatically update the candidate pool count when any of the filter values change
    useEffect(() => {
        // console.log(
        //     `Updating filtered candidate pool count... current filters:`,
        //     filterRef.current,
        // )
        getFilteredCandidatePoolCount()
    }, [
        shortlistedSelected,
        networkLinksSelectedInFilters,
        minRelationshipScore,
        mustBeInBooleanSearchResults,
        mustHaveRelationship,
        getFilteredCandidatePoolCount,
    ])

    function convertShortlistedSelectedToFilterValue(
        shortlistedSelected: string[],
    ): ShortlistFilterValues[] {
        const values: ShortlistFilterValues[] = []
        if (shortlistedSelected.includes('yes')) {
            values.push(ShortlistFilterValues.yes)
        }
        if (shortlistedSelected.includes('no')) {
            values.push(ShortlistFilterValues.no)
        }
        if (shortlistedSelected.includes('blanks')) {
            values.push(ShortlistFilterValues.none)
        }

        return values
    }

    function convertOrderByToSortBy(orderBy: string): {
        sortBy: SortBy
        direction: SortByDirection
    } {
        const [sortBy, direction] = orderBy.split('_')
        return {
            sortBy: SortBy[sortBy as keyof typeof SortBy],
            direction:
                SortByDirection[direction as keyof typeof SortByDirection],
        }
    }

    function convertMinRelationshipScoreToNumber(
        minRelationshipScore: string,
    ): number | undefined {
        if (minRelationshipScore === 'min_relationship_score_any') {
            return undefined
        }

        const parts = minRelationshipScore.split('_')

        // e.g. min_relationship_score_40 returns 40
        return parseInt(parts[parts.length - 1], 10)
    }

    async function handleFilterApplyClick() {
        setCurrentPageNumber(1)
        // console.log(`Filter apply clicked: filtersRef.current()`, {
        //     currentPageNumber: 1,
        //     perPage,
        //     networkLinksSelected: networkLinksSelectedInFilters,
        //     minRelationshipScore,
        //     mustBeInBooleanSearchResults,
        //     mustHaveRelationship,
        //     shortlistedSelected,
        //     orderBy,
        // })
        await loadResults(
            searchSession!.sessionSummary,
            {
                currentPageNumber: 1,
                perPage,
                networkLinksSelected: networkLinksSelectedInFilters,
                minRelationshipScore,
                mustBeInBooleanSearchResults,
                mustHaveRelationship,
                shortlistedSelected,
                orderBy,
            },
            true,
        )
    }
    async function handleFilterResetClick() {
        // reset to defaults
        const resetFilterData = resetFilters(networkLinkIdsSetInThisSearch)
        if (searchSession) {
            await loadResults(
                searchSession.sessionSummary,
                resetFilterData,
                true,
            )
        }
    }

    async function networkLinksSelectedChanged(selectedIds: string[]) {
        setNetworkLinksSelectedInFilters(selectedIds)
        setFiltersAreDirty(true)
    }
    async function shortlistedSelectedChanged(selectedIds: string[]) {
        setShortlistedSelected(selectedIds)
        setFiltersAreDirty(true)
    }
    async function minRelationshipScoreChanged(value: string) {
        setMinRelationshipScore(value)
        setFiltersAreDirty(true)
    }
    async function orderByChanged(value: string) {
        setOrderBy(value)
        setFiltersAreDirty(true)
    }
    async function mustBeInBooleanSearchResultsChanged(checked: boolean) {
        setMustBeInBooleanSearchResults(checked)
        setFiltersAreDirty(true)
    }
    async function mustHaveRelationshipChanged(checked: boolean) {
        setMustHaveRelationship(checked)
        setFiltersAreDirty(true)
    }
    // ##### filter functions - end #####

    const pageChanged = useCallback(
        async (pageNumber: number) => {
            // console.log(
            //     `Page changed to: ${pageNumber} Per page: ${filterRef.current.perPage}`,
            // )
            setCurrentPageNumber(pageNumber)

            if (searchSession) {
                const filters = {
                    ...filterRef.current,
                    currentPageNumber: pageNumber,
                }

                await loadResults(searchSession.sessionSummary, filters, true)
            }
        },
        [searchSession, loadResults],
    )

    const perPageChanged = useCallback(
        async (newPerPage: number) => {
            // console.log(
            //     `Per page changed to: ${newPerPage} Page number: ${currentPageNumber} BUT we'll use 1`,
            // )
            setPerPage(newPerPage)
            setCurrentPageNumber(1)

            if (searchSession) {
                const filters = {
                    ...filterRef.current,
                    currentPageNumber: 1,
                    perPage: newPerPage,
                }

                await loadResults(searchSession.sessionSummary, filters, true)
            }
        },
        [searchSession, loadResults],
    )

    const handleCandidateSelection = useCallback((candidateId: string) => {
        setSelectedCandidateId(candidateId)
        setRightPaneMode('candidate')
    }, [])

    return (
        <LayoutSplitView
            activeMenuItem={MenuItems.HelixSearch}
            className="bg-gray-50"
            rightPane={(() => {
                switch (rightPaneMode) {
                    case 'boolean_search_help':
                        return <HelixBooleanSearchHelp />
                    case 'saved_searches':
                        return <div>saved searches</div>
                    case 'candidate':
                        return (
                            <HelixCandidateRightPane
                                candidateId={selectedCandidateId!}
                                networkLinksPersonLiteCache={
                                    personLiteCacheRef.current
                                }
                                candidatesPersonCache={personCacheRef.current}
                                companyCache={companyCacheRef.current}
                                candidateConnections={
                                    resolvedPersonLites.find(
                                        (p) =>
                                            p.personId === selectedCandidateId,
                                    )?.connectionDetails ?? []
                                }
                            />
                        )
                    case 'network_seed':
                        return <div>NetworkSeedRightPane</div>
                    case 'company':
                        return <div>CompanyRightPane</div>
                    default:
                        throw new Error(`Invalid mode: ${rightPaneMode}`)
                }
            })()}
        >
            {roleId && (
                <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                    <h1 className="text-xl font-bold mb-4">Role Details</h1>
                    If a roleId URL param was passed we'll show the role details
                    here so the user knows what Role the search is for.
                </div>
            )}
            <div className="bg-white border-l-4 border-ctxt-blue rounded-2xl shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                        <FontAwesomeIcon
                            icon={faLink}
                            className="text-ctxt-blue"
                        />
                        Network Links
                    </div>
                    <Button
                        style="secondary"
                        type="button"
                        onClick={() => setIsModalOpen(true)}
                    >
                        <FontAwesomeIcon
                            icon={faPlusCircle}
                            className={`mr-2`}
                        />
                        Add Network Links
                    </Button>
                </div>

                {/* TODO : 77 - Bug, We should be able to run the search without selecting any network links */}
                <NetworkLinks
                    networkLinks={networkLinksSelectedInNetworkLinks}
                    onRemove={handleRemoveNetworkLink}
                />
                {/* <p>
                    If a roleId URL param was passed we'll pre-populate this
                    with the network seeds from the Role
                </p>
               */}
            </div>

            <div className="bg-white border-l-4 border-ctxt-blue rounded-2xl shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="text-ctxt-blue"
                        />
                        Boolean Search
                    </div>
                    <Button
                        style="secondary"
                        type="button"
                        onClick={() => {
                            setRightPaneMode('boolean_search_help')
                        }}
                        size="small"
                    >
                        Show Help
                    </Button>
                </div>
                {/* <div className="text-right italic">
                    Clicking Show Help should show the boolean help Note in the
                    right pane
                </div> */}
                <FieldSearchEditor
                    value={query}
                    onChange={(newQuery) => {
                        setQuery(newQuery)
                    }}
                    onSearch={() => {}}
                />
                {/* TODO : 77 - Do we enable search if no network links are selected? */}
                <div className="mt-4 flex items-center gap-4 justify-between">
                    <Button
                        style="primary"
                        type="button"
                        onClick={() => {
                            search()
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="text-white mr-2"
                            title="Search"
                        />
                        Search
                    </Button>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900">
                            Ignore Retired People
                        </span>
                        <ToggleSwitch3
                            value={ignoreRetiredPeople === true}
                            onChange={(checked) =>
                                setIgnoreRetiredPeople(checked)
                            }
                        />
                    </div>
                </div>

                {showSearchProgress && searchSession && (
                    <HelixSearchProgressModal
                        session={searchSession}
                        onClose={() => setShowSearchProgress(false)}
                        showResults={showResults}
                    />
                )}

                {showDataLoading && (
                    <HelixSearchDataLoadingModal
                        onClose={() => setShowDataLoading(false)}
                    />
                )}
            </div>
            {errorMsg && (
                <Note type={NoteType.ERROR} containerClassNames="bg-white">
                    {errorMsg}
                </Note>
            )}
            {showResults && !errorMsg && (
                <div>
                    <div
                        className="bg-white rounded-2xl shadow-md p-6 mb-6"
                        ref={insightsRef}
                    >
                        <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 pb-2 mb-4">
                            <FontAwesomeIcon
                                icon={faChartBar}
                                className="text-ctxt-blue"
                            />
                            Search Insights
                        </div>
                        <HelixSearchInsights
                            total={
                                searchSession?.sessionSummary
                                    ?.totalUniqueCandidatePoolCount ?? 0
                            }
                            matchesCriteria={
                                searchSession?.sessionSummary
                                    ?.booleanSearchEvalPersonCount ?? 0
                            }
                            inNetwork={
                                searchSession?.sessionSummary
                                    ?.relationshipEvalPersonCount ?? 0
                            }
                            matchesBoth={
                                searchSession?.sessionSummary
                                    ?.fullyQualifiedPersonCount ?? 0
                            }
                        />
                    </div>
                    <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                        <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 pb-2 mb-4">
                            <FontAwesomeIcon
                                icon={faFilter}
                                className="text-ctxt-blue"
                            />
                            Filters
                        </div>
                        <HelixSearchFilters
                            roleId={roleId}
                            availableNetworkLinkIds={
                                networkLinkIdsSetInThisSearch
                            }
                            personLiteCache={personLiteCacheRef.current}
                            applyClick={handleFilterApplyClick}
                            resetClick={handleFilterResetClick}
                            networkLinksSelected={networkLinksSelectedInFilters}
                            shortlistedSelected={shortlistedSelected}
                            minRelationshipScore={minRelationshipScore}
                            orderBy={orderBy}
                            mustBeInBooleanSearchResults={
                                mustBeInBooleanSearchResults
                            }
                            mustHaveRelationship={mustHaveRelationship}
                            filteredCandidatePoolCount={
                                filteredCandidatePoolCount
                            }
                            filteredCandidatePoolCountIsLoading={
                                filteredCandidatePoolCountIsLoading
                            }
                            networkLinksSelectedChanged={
                                networkLinksSelectedChanged
                            }
                            shortlistedSelectedChanged={
                                shortlistedSelectedChanged
                            }
                            minRelationshipScoreChanged={
                                minRelationshipScoreChanged
                            }
                            orderByChanged={orderByChanged}
                            mustBeInBooleanSearchResultsChanged={
                                mustBeInBooleanSearchResultsChanged
                            }
                            mustHaveRelationshipChanged={
                                mustHaveRelationshipChanged
                            }
                            filtersAreDirty={filtersAreDirty}
                        />
                    </div>
                    {searchResults.length > 0 ? (
                        <>
                            <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                                <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 pb-2 mb-4">
                                    <FontAwesomeIcon
                                        icon={faHexagonNodes}
                                        className="text-ctxt-blue"
                                    />
                                    Candidate Network
                                </div>
                                <HelixSearchNetworkView
                                    resolvedPersons={resolvedPersonLites}
                                    networkLinksPersonCache={
                                        personLiteCacheRef.current
                                    }
                                    candidatesPersonCache={
                                        personCacheRef.current
                                    }
                                    companyCache={companyCacheRef.current}
                                    onCandidateSelection={(candidateId) => {
                                        setSelectedCandidateId(candidateId)
                                        setRightPaneMode('candidate')
                                    }}
                                />
                            </div>
                            <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                                <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 pb-2 mb-4">
                                    <FontAwesomeIcon
                                        icon={faList}
                                        className="text-ctxt-blue"
                                    />
                                    Candidate List
                                </div>
                                <HelixCandidateResultList
                                    personCache={personCacheRef.current}
                                    resolvedPersonLites={resolvedPersonLites}
                                    personLiteCache={personLiteCacheRef.current}
                                    companyCache={companyCacheRef.current}
                                    candidates={helixCandidateLiteDtos}
                                    perPage={perPage}
                                    currentPageNumber={currentPageNumber}
                                    totalResultCount={totalResultCount}
                                    pageChanged={pageChanged}
                                    perPageChanged={perPageChanged}
                                    onCandidateSelection={
                                        handleCandidateSelection
                                    }
                                />
                            </div>
                        </>
                    ) : (
                        <Note
                            type={NoteType.WARNING}
                            containerClassNames="bg-white mb-16"
                        >
                            No results. Try refining the filters or the search
                            criteria.
                        </Note>
                    )}
                </div>
            )}
            {isModalOpen && (
                <AddNetworkLinksModal
                    currentNetworkLinksIds={
                        networkLinksIdsSelectedInNetworkLinks
                    }
                    onClose={addNetworkLinksModalClosed}
                    onLiveSelectChange={handleLiveNetworkLinkSelection}
                />
            )}
        </LayoutSplitView>
    )
}
